export type SortDirection = 'asc' | 'desc';

export interface SortState<K extends string> {
  key: K;
  direction: SortDirection;
}

export interface PageSlice<T> {
  items: T[];
  page: number;
  pageCount: number;
  total: number;
  from: number;
  to: number;
}

export interface TableQuery<K extends string> {
  page: number;
  pageSize: number;
  sortKey: K;
  sortDirection: SortDirection;
}

export class LatestRequestGuard {
  private version = 0;

  begin(): number {
    this.version += 1;
    return this.version;
  }

  invalidate(): void {
    this.version += 1;
  }

  isLatest(version: number): boolean {
    return version === this.version;
  }
}

type SortableValue = string | number | boolean | Date | null | undefined;

export type SortAccessors<T, K extends string> = Record<K, (item: T) => SortableValue>;

export function toggleSort<K extends string>(current: SortState<K>, key: K): SortState<K> {
  if (current.key === key) {
    return {
      key,
      direction: current.direction === 'asc' ? 'desc' : 'asc'
    };
  }

  return {
    key,
    direction: 'asc'
  };
}

export function sortDirection<K extends string>(
  state: SortState<K>,
  key: K
): SortDirection | null {
  return state.key === key ? state.direction : null;
}

export function sortItems<T, K extends string>(
  items: readonly T[],
  state: SortState<K>,
  accessors: SortAccessors<T, K>
): T[] {
  const accessor = accessors[state.key];
  const direction = state.direction === 'asc' ? 1 : -1;

  return [...items].sort((leftItem, rightItem) => {
    const left = normalizeSortValue(accessor(leftItem));
    const right = normalizeSortValue(accessor(rightItem));

    if (left < right) {
      return -1 * direction;
    }

    if (left > right) {
      return 1 * direction;
    }

    return 0;
  });
}

export function paginateItems<T>(
  items: readonly T[],
  page: number,
  pageSize: number
): PageSlice<T> {
  const total = items.length;
  const pages = getPageCount(total, pageSize);
  const currentPage = clampPage(page, pages);
  const startIndex = (currentPage - 1) * pageSize;

  return {
    items: items.slice(startIndex, startIndex + pageSize),
    page: currentPage,
    pageCount: pages,
    total,
    from: total === 0 ? 0 : startIndex + 1,
    to: Math.min(total, startIndex + pageSize)
  };
}

export function emptyPageSlice<T>(): PageSlice<T> {
  return {
    items: [],
    page: 1,
    pageCount: 1,
    total: 0,
    from: 0,
    to: 0
  };
}

export function adjustedPageAfterMutation<T>(
  requestedPage: number,
  slice: PageSlice<T>
): number | null {
  const clampedPage = clampPage(requestedPage, slice.pageCount);

  if (clampedPage !== requestedPage) {
    return clampedPage;
  }

  if (requestedPage > 1 && slice.total > 0 && slice.items.length === 0) {
    return requestedPage - 1;
  }

  return null;
}

export function applyTableQuery<T, K extends string>(
  items: readonly T[],
  query: TableQuery<K>,
  accessors: SortAccessors<T, K>
): PageSlice<T> {
  return paginateItems(
    sortItems(items, { key: query.sortKey, direction: query.sortDirection }, accessors),
    query.page,
    query.pageSize
  );
}

export function getPageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export function clampPage(page: number, pageCount: number): number {
  return Math.min(Math.max(1, page), Math.max(1, pageCount));
}

function normalizeSortValue(value: SortableValue): string | number {
  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value === 'number') {
    return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  if (value == null) {
    return '';
  }

  return String(value).toLocaleLowerCase();
}
